import {
	ActionIcon,
	Badge,
	Box,
	Button,
	Group,
	Paper as MantinePaper,
	SimpleGrid,
	Stack as MantineStack,
	Text as MantineText,
	Tooltip
} from "@mantine/core";
import { IconCheck, IconInfoCircle, IconSparkles } from "@tabler/icons-react";
import { useColorScheme } from "@mui/material";
import { usePopUp } from "@hooks/usePopUp.tsx";
import React, { JSX, useState } from "react";
import { SvgIntencion, SvgMasterGold, SvgTropical } from '@assets/images/svgs/ProductSvgIcons.tsx';

type themeSelectOption = {
	svg?: JSX.Element;
	title: string;
	description: string;
	mode: 'light' | 'dark';
	colorScheme: 'tropical' | 'intencion' | 'masterGold';
	themeColor: string;
	glowColor: string;
	mantineColor: string;
	badge: string;
};

const themes = {
	tropical: {
		svg: (
			<Box
				style={{
					maxHeight: '100%',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center'
				}}
			>
				<SvgTropical slotProps={{ svg: { height: 180 } }} />
			</Box>
		),
		title: 'Tropical Energy',
		description: 'Visual vibrante e dinâmico com tema claro.',
		mode: 'light',
		colorScheme: 'tropical',
		themeColor: '#f59f00',
		glowColor: 'rgba(245, 159, 0, 0.45)',
		mantineColor: 'yellow',
		badge: 'Vibrante • Light'
	} as themeSelectOption,
	intencion: {
		svg: (
			<Box
				style={{
					maxHeight: '100%',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center'
				}}
			>
				<SvgIntencion slotProps={{ svg: { height: 180 } }} />
			</Box>
		),
		title: 'Intencion',
		description: 'Visual suave com tons modernos e equilibrados.',
		mode: 'light',
		colorScheme: 'intencion',
		themeColor: '#1b458f',
		glowColor: 'rgba(27, 69, 143, 0.45)',
		mantineColor: 'indigo',
		badge: 'Equilibrado • Light'
	} as themeSelectOption,
	masterGold: {
		svg: (
			<Box
				style={{
					maxHeight: '100%',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center'
				}}
			>
				<SvgMasterGold slotProps={{ svg: { height: 180 } }} />
			</Box>
		),
		title: 'Master Gold',
		description: 'Modo escuro premium elegante com detalhes dourados.',
		mode: 'dark',
		colorScheme: 'masterGold',
		themeColor: '#d4af37',
		glowColor: 'rgba(212, 175, 55, 0.55)',
		mantineColor: 'yellow',
		badge: 'Premium • Dark'
	} as themeSelectOption,
} as const;

export function FirstRunSelectTheme() {
	const itemData = [themes.tropical, themes.intencion, themes.masterGold];
	const { colorScheme, setMode, setColorScheme } = useColorScheme();
	const { closePopUp } = usePopUp();

	const [changeTimeout, setChangeTimeout] = useState<NodeJS.Timeout | null>(null);

	const handleChange = (props: { mode: 'dark' | 'light'; scheme: any; apply: boolean }) => {
		setMode(props.mode);
		setColorScheme(props.scheme as any);
		if (props.apply) {
			localStorage.setItem('userThemeSelected', props.mode);
		}
	};

	return (
		<MantineStack gap="md" p="xs">
			<style>{`
				@keyframes themeCanShakeAndFloat {
					0% {
						transform: translateY(0) scale(1) rotate(0deg);
					}
					15% {
						transform: translateY(-8px) scale(1.05) rotate(-5deg);
					}
					30% {
						transform: translateY(-12px) scale(1.08) rotate(5deg);
					}
					45% {
						transform: translateY(-9px) scale(1.07) rotate(-3deg);
					}
					60% {
						transform: translateY(-7px) scale(1.06) rotate(2deg);
					}
					75% {
						transform: translateY(-6px) scale(1.05) rotate(-1deg);
					}
					100% {
						transform: translateY(-6px) scale(1.05) rotate(0deg);
					}
				}

				@keyframes themeCanGentleFloat {
					0%, 100% {
						transform: translateY(-6px) scale(1.05) rotate(0deg);
					}
					50% {
						transform: translateY(-12px) scale(1.07) rotate(-1deg);
					}
				}

				.theme-select-card {
					position: relative;
					overflow: hidden;
					cursor: pointer;
					display: flex;
					flex-direction: column;
					justifyContent: space-between;
					text-align: center;
					transition: transform 0.28s cubic-bezier(0.34, 1.45, 0.64, 1),
					            box-shadow 0.28s ease,
					            border-color 0.28s ease,
					            background 0.28s ease;
				}

				.theme-select-card::before {
					content: "";
					position: absolute;
					top: 0;
					left: -150%;
					width: 100%;
					height: 100%;
					background: linear-gradient(
						115deg,
						transparent 25%,
						rgba(255, 255, 255, 0.16) 50%,
						transparent 75%
					);
					transform: skewX(-20deg);
					pointer-events: none;
					z-index: 2;
				}

				.theme-select-card:hover::before {
					left: 150%;
					transition: left 0.7s cubic-bezier(0.4, 0, 0.2, 1);
				}

				.theme-select-card:hover {
					transform: translateY(-6px) scale(1.02);
					box-shadow: 0 16px 32px -4px var(--card-glow, rgba(0, 0, 0, 0.2)), 0 0 0 2px var(--card-accent, #228be6);
					border-color: var(--card-accent, #228be6) !important;
				}

				.theme-select-card:active {
					transform: translateY(-2px) scale(0.99);
				}

				.theme-select-card .can-group {
					transform-origin: bottom center;
					transition: transform 0.35s cubic-bezier(0.25, 1, 0.5, 1), filter 0.35s ease;
				}

				.theme-select-card:hover .can-group {
					animation: themeCanShakeAndFloat 0.6s cubic-bezier(0.36, 0.07, 0.19, 0.97) forwards,
					           themeCanGentleFloat 2.6s ease-in-out 0.6s infinite alternate;
					filter: drop-shadow(0 10px 18px var(--card-glow, rgba(0, 0, 0, 0.3))) !important;
				}

				.theme-select-card.is-active {
					border-color: var(--card-accent, #228be6);
					box-shadow: 0 0 0 1.5px var(--card-accent, #228be6), 0 8px 20px -6px var(--card-glow, rgba(0, 0, 0, 0.15));
				}
			`}</style>

			<MantineText size="sm" c="dimmed">
				É sua primeira vez por aqui! Escolha o tema inicial de sua preferência para personalizar a experiência do sistema:
			</MantineText>

			<SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
				{itemData.map((item) => {
					const isSelected = (colorScheme as string) === item.colorScheme;

					return (
						<MantinePaper
							key={item.title}
							className={`theme-select-card ${isSelected ? 'is-active' : ''}`}
							withBorder
							radius="md"
							p="md"
							style={{
								'--card-accent': item.themeColor,
								'--card-glow': item.glowColor
							} as React.CSSProperties}
							onMouseEnter={() => {
								if (changeTimeout) {
									clearTimeout(changeTimeout);
								}
								setChangeTimeout(
									setTimeout(() => {
										handleChange({ mode: item.mode, scheme: item.colorScheme, apply: false });
									}, 350)
								);
							}}
							onMouseLeave={() => {
								if (changeTimeout) {
									clearTimeout(changeTimeout);
								}
							}}
							onClick={() => {
								handleChange({ mode: item.mode, scheme: item.colorScheme, apply: true });
								closePopUp();
							}}
						>
							<Group justify="space-between" align="center" mb="xs">
								<Badge
									size="xs"
									variant={isSelected ? "filled" : "light"}
									color={item.mantineColor}
									leftSection={isSelected ? <IconCheck size={10} stroke={3} /> : undefined}
								>
									{isSelected ? "Ativo" : item.badge}
								</Badge>

								<Tooltip label={item.description} withArrow>
									<ActionIcon size="xs" variant="subtle" color="gray" onClick={(e) => e.stopPropagation()}>
										<IconInfoCircle size={14} />
									</ActionIcon>
								</Tooltip>
							</Group>

							<Box
								py="sm"
								style={{
									minHeight: 200,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									borderRadius: 8,
									background: 'radial-gradient(circle at center, rgba(128, 128, 128, 0.05) 0%, transparent 70%)'
								}}
							>
								{item.svg}
							</Box>

							<MantineStack gap={6} mt="sm">
								<MantineText fw={700} size="sm">
									{item.title}
								</MantineText>
								<MantineText size="xs" c="dimmed" ta="center" style={{ minHeight: 32 }}>
									{item.description}
								</MantineText>

								<Button
									size="xs"
									variant={isSelected ? "filled" : "light"}
									color={item.mantineColor}
									radius="md"
									fullWidth
									mt="xs"
									leftSection={isSelected ? <IconCheck size={14} /> : <IconSparkles size={14} />}
								>
									{isSelected ? "Tema Selecionado" : "Escolher este tema"}
								</Button>
							</MantineStack>
						</MantinePaper>
					);
				})}
			</SimpleGrid>
		</MantineStack>
	);
}